import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost } from '../api/client';
import type { LeadWithIntelligence, LeadActivity } from '../api/types';
import {
  timeAgo,
  stageLabel,
  pad2,
  countMissing,
  completeness,
  getEnrichFields,
} from '../lib/format';
import styles from './OperatePage.module.css';

const STAGE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'new', label: 'Nuevo' },
  { value: 'researching', label: 'Investigando' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'conversation', label: 'Conversación' },
  { value: 'diagnostic', label: 'Diagnóstico' },
  { value: 'proposal', label: 'Propuesta' },
  { value: 'negotiation', label: 'Negociación' },
  { value: 'won', label: 'Ganado' },
  { value: 'lost', label: 'Perdido' },
  { value: 'dormant', label: 'Inactivo' },
];

const TIER_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'T1', label: 'T1' },
  { value: 'T2', label: 'T2' },
  { value: 'T3', label: 'T3' },
];

const MISSING_OPTIONS = [
  { value: '', label: 'Cualquiera' },
  { value: 'contactEmail', label: 'Sin email' },
  { value: 'contactPhone', label: 'Sin teléfono' },
  { value: 'contactName', label: 'Sin contacto' },
  { value: 'websiteUrl', label: 'Sin web' },
  { value: 'painPoint', label: 'Sin pain point' },
];

const TIER_ORDER: Record<string, number> = { T1: 0, T2: 1, T3: 2 };

const TERMINAL_STAGES = new Set(['lost', 'dormant']);
const POSITIVE_STAGES = new Set(['won']);

const TRANSITIONS: Record<string, string[]> = {
  new: ['researching', 'outreach', 'lost', 'dormant'],
  researching: ['outreach', 'lost', 'dormant'],
  outreach: ['conversation', 'researching', 'lost', 'dormant'],
  conversation: ['diagnostic', 'outreach', 'lost', 'dormant'],
  diagnostic: ['proposal', 'conversation', 'lost', 'dormant'],
  proposal: ['negotiation', 'diagnostic', 'lost', 'dormant'],
  negotiation: ['won', 'lost', 'proposal', 'dormant'],
  won: [],
  lost: [],
  dormant: ['researching', 'outreach', 'conversation'],
};

const ENRICH_FIELDS = getEnrichFields();

type Result =
  | null
  | { type: 'enrich'; data: Record<string, string>; confidence?: string }
  | { type: 'outreach'; data: { subject: string; body: string } }
  | { type: 'followup'; data: { action: string; reasoning: string; urgency: string } }
  | { type: 'error'; message: string };

interface FiltersState {
  search: string;
  stage: string;
  tier: string;
  missing: string;
}

interface SelectionState {
  id: string | null;
  lead: LeadWithIntelligence | null;
  activities: LeadActivity[];
  panelLoading: boolean;
}

interface OpsState {
  result: Result;
  busy: Set<string>;
}

interface ListState {
  leads: LeadWithIntelligence[];
  loading: boolean;
}

interface PanelState {
  id: string | null;
  lead: LeadWithIntelligence | null;
  activities: LeadActivity[];
  panelLoading: boolean;
  result: Result;
  busy: Set<string>;
}

function useOperate() {
  const [list, setList] = useState<ListState>({ leads: [], loading: true });
  const [filters, setFilters] = useState<FiltersState>({
    search: '',
    stage: '',
    tier: '',
    missing: '',
  });
  const [panel, setPanel] = useState<PanelState>({
    id: null,
    lead: null,
    activities: [],
    panelLoading: false,
    result: null,
    busy: new Set(),
  });

  // Load leads on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = (await apiGet('/api/leads?limit=5000')) as {
          leads: LeadWithIntelligence[];
        };
        if (!cancelled) setList({ leads: data.leads || [], loading: false });
      } catch {
        if (!cancelled) setList({ leads: [], loading: false });
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Filter & sort
  const filtered = useMemo(() => {
    const q = filters.search.toLowerCase().trim();
    let out = list.leads.filter((l) => {
      if (q) {
        const hay = [
          l.companyName,
          l.contactName,
          l.contactEmail,
          l.sector,
        ].join(' ');
        if (!hay.toLowerCase().includes(q)) return false;
      }
      if (filters.stage && l.stage !== filters.stage) return false;
      if (filters.tier && l.tier !== filters.tier) return false;
      if (filters.missing && ((l as unknown as Record<string, unknown>)[filters.missing])) return false;
      return true;
    });
    out.sort((a, b) => {
      const da = TIER_ORDER[a.tier ?? ''] ?? 3;
      const db = TIER_ORDER[b.tier ?? ''] ?? 3;
      if (da !== db) return da - db;
      return (b.compositeScore || 0) - (a.compositeScore || 0);
    });
    return out;
  }, [list.leads, filters]);

  // Clear panel when no lead is selected
  useEffect(() => {
    if (!panel.id) {
      setPanel((prev) => ({
        ...prev,
        lead: null,
        activities: [],
        panelLoading: false,
        result: null,
      }));
    }
  }, [panel.id]);

  // Fetch lead detail when selection changes
  useEffect(() => {
    if (!panel.id) return;
    let cancelled = false;
    async function loadDetail() {
      let lead: LeadWithIntelligence | null = null;
      let activities: LeadActivity[] = [];
      try {
        const data = (await apiGet(`/api/leads/${panel.id}`)) as {
          lead: LeadWithIntelligence;
          activities: LeadActivity[];
        };
        lead = data.lead;
        activities = data.activities || [];
      } catch {
        lead = null;
        activities = [];
      }
      if (!cancelled) {
        setPanel((prev) => ({ ...prev, lead, activities, panelLoading: false }));
      }
    }
    setPanel((prev) => ({ ...prev, panelLoading: true, result: null }));
    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [panel.id]);

  const selectLead = useCallback((id: string) => {
    setPanel((prev) => ({ ...prev, id }));
  }, []);

  const withBusy = useCallback(async (action: string, fn: () => Promise<void>) => {
    setPanel((prev) => ({ ...prev, busy: new Set(prev.busy).add(action) }));
    try {
      await fn();
    } finally {
      setPanel((prev) => {
        const next = new Set(prev.busy);
        next.delete(action);
        return { ...prev, busy: next };
      });
    }
  }, []);

  const transitionStage = useCallback(
    async (toStage: string) => {
      if (!panel.id) return;
      try {
        await apiPost(`/api/leads/${panel.id}/transition`, { toStage });
        const [listData, detailData] = await Promise.all([
          apiGet('/api/leads?limit=5000') as Promise<{
            leads: LeadWithIntelligence[];
          }>,
          apiGet(`/api/leads/${panel.id}`) as Promise<{
            lead: LeadWithIntelligence;
            activities: LeadActivity[];
          }>,
        ]);
        setList((prev) => ({ ...prev, leads: listData.leads || [] }));
        setPanel((prev) => ({
          ...prev,
          lead: detailData.lead,
          activities: detailData.activities || [],
          result: null,
        }));
      } catch (err) {
        setPanel((prev) => ({
          ...prev,
          result: {
            type: 'error',
            message: err instanceof Error ? err.message : 'Error de transición',
          },
        }));
      }
    },
    [panel.id],
  );

  const runEnrich = useCallback(async () => {
    if (!panel.id) return;
    await withBusy('enrich', async () => {
      const data = (await apiPost(`/api/llm/enrich/${panel.id}`)) as {
        success: boolean;
        data?: Record<string, string>;
        error?: string;
        sources?: string[];
      };
      if (!data.success) {
        setPanel((prev) => ({
          ...prev,
          result: { type: 'error', message: data.error || 'Error al enriquecer' },
        }));
        return;
      }
      setPanel((prev) => ({
        ...prev,
        result: {
          type: 'enrich',
          data: data.data || {},
          confidence: data.sources?.join(', '),
        },
      }));
    });
  }, [panel.id, withBusy]);

  const applyAllEnrich = useCallback(async () => {
    if (!panel.id || panel.result?.type !== 'enrich') return;
    const enrichResult = panel.result;
    await withBusy('applyAll', async () => {
      const fields: Record<string, string> = {};
      for (const f of ENRICH_FIELDS) {
        const v = enrichResult.data[f.key];
        if (v != null && v !== '') fields[f.key] = v;
      }
      if (Object.keys(fields).length === 0) {
        setPanel((prev) => ({
          ...prev,
          result: { type: 'error', message: 'Sin cambios' },
        }));
        return;
      }
      await apiPost('/api/llm/apply-suggestion', { leadId: panel.id, fields });
      const [listData, detailData] = await Promise.all([
        apiGet('/api/leads?limit=5000') as Promise<{
          leads: LeadWithIntelligence[];
        }>,
        apiGet(`/api/leads/${panel.id}`) as Promise<{
          lead: LeadWithIntelligence;
          activities: LeadActivity[];
        }>,
      ]);
      setList((prev) => ({ ...prev, leads: listData.leads || [] }));
      setPanel((prev) => ({
        ...prev,
        lead: detailData.lead,
        activities: detailData.activities || [],
        result: null,
      }));
    });
  }, [panel.id, panel.result, withBusy]);

  const runOutreach = useCallback(async () => {
    if (!panel.id) return;
    await withBusy('outreach', async () => {
      const data = (await apiPost(`/api/llm/generate-outreach/${panel.id}`, {})) as {
        success: boolean;
        data?: { subject: string; body: string; templateUsed?: string };
        error?: string;
      };
      if (!data.success) {
        setPanel((prev) => ({
          ...prev,
          result: { type: 'error', message: data.error || 'Error al generar outreach' },
        }));
        return;
      }
      setPanel((prev) => ({
        ...prev,
        result: {
          type: 'outreach',
          data: data.data || { subject: '', body: '' },
        },
      }));
    });
  }, [panel.id, withBusy]);

  const copyOutreach = useCallback(async () => {
    if (panel.result?.type !== 'outreach') return;
    const text = `Subject: ${panel.result.data.subject}\n\n${panel.result.data.body}`;
    await navigator.clipboard.writeText(text);
  }, [panel.result]);

  const runFollowup = useCallback(async () => {
    if (!panel.id) return;
    await withBusy('followup', async () => {
      const data = (await apiPost(`/api/llm/suggest-followup/${panel.id}`)) as {
        success: boolean;
        data?: { action: string; reasoning: string; urgency: string };
        error?: string;
      };
      if (!data.success) {
        setPanel((prev) => ({
          ...prev,
          result: { type: 'error', message: data.error || 'Error al sugerir follow-up' },
        }));
        return;
      }
      setPanel((prev) => ({
        ...prev,
        result: {
          type: 'followup',
          data: data.data || { action: '', reasoning: '', urgency: 'media' },
        },
      }));
    });
  }, [panel.id, withBusy]);

  const dismissResult = useCallback(() => {
    setPanel((prev) => ({ ...prev, result: null }));
  }, []);

  const lastEnrich = useMemo(() => {
    for (const a of panel.activities) {
      if (a.type === 'enrichment' || a.type === 'llm_action') return a;
    }
    return null;
  }, [panel.activities]);

  const lastOutreach = useMemo(() => {
    for (const a of panel.activities) {
      if (a.type === 'llm_action' || a.type === 'email_sent') return a;
    }
    return null;
  }, [panel.activities]);

  return {
    list,
    filters,
    filtered,
    panel,
    setFilters,
    selectLead,
    transitionStage,
    runEnrich,
    applyAllEnrich,
    runOutreach,
    copyOutreach,
    runFollowup,
    dismissResult,
    lastEnrich,
    lastOutreach,
  };
}

export default function OperatePage() {
  const {
    list,
    filters,
    filtered,
    panel,
    setFilters,
    selectLead,
    transitionStage,
    runEnrich,
    applyAllEnrich,
    runOutreach,
    copyOutreach,
    runFollowup,
    dismissResult,
    lastEnrich,
    lastOutreach,
  } = useOperate();

  return (
    <div className={styles.opShell}>
      <LeadList
        loading={list.loading}
        filters={filters}
        filtered={filtered}
        selectedId={panel.id}
        onFilterChange={(next) => setFilters((prev) => ({ ...prev, ...next }))}
        onSelectLead={selectLead}
      />

      <LeadDossier
        selection={{
          id: panel.id,
          lead: panel.lead,
          activities: panel.activities,
          panelLoading: panel.panelLoading,
        }}
        ops={{ result: panel.result, busy: panel.busy }}
        lastEnrich={lastEnrich}
        lastOutreach={lastOutreach}
        onTransition={transitionStage}
        onEnrich={runEnrich}
        onApplyAll={applyAllEnrich}
        onOutreach={runOutreach}
        onCopyOutreach={copyOutreach}
        onFollowup={runFollowup}
        onDismiss={dismissResult}
      />
    </div>
  );
}

function LeadList({
  loading,
  filters,
  filtered,
  selectedId,
  onFilterChange,
  onSelectLead,
}: {
  loading: boolean;
  filters: FiltersState;
  filtered: LeadWithIntelligence[];
  selectedId: string | null;
  onFilterChange: (next: Partial<FiltersState>) => void;
  onSelectLead: (id: string) => void;
}) {
  return (
    <section className={styles.opListWrap} aria-label="Leads">
      <header className={styles.opMasthead}>
        <div className={styles.opMastheadInner}>
          <p className={styles.opKicker}>Operar</p>
          <h1 className={styles.opHead}>Dossier del día</h1>
          <p className={styles.opDeck}>
            Filtra, elige un lead y actúa sin cambiar de vista. Una fila, una decisión.
          </p>
        </div>
        <div className={styles.opToolbar}>
          <div className={styles.opSearchWrap}>
            <span className={styles.opSearchGlyph} aria-hidden="true">
              &#9906;
            </span>
            <input
              type="search"
              className={styles.opSearch}
              placeholder="Buscar empresa, contacto…"
              autoComplete="off"
              value={filters.search}
              onChange={(e) => onFilterChange({ search: e.target.value })}
            />
          </div>
          <div className={styles.opFilters}>
            <label className={styles.opFilter}>
              <span>Stage</span>
              <select
                value={filters.stage}
                onChange={(e) => onFilterChange({ stage: e.target.value })}
              >
                {STAGE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.opFilter}>
              <span>Tier</span>
              <select
                value={filters.tier}
                onChange={(e) => onFilterChange({ tier: e.target.value })}
              >
                {TIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.opFilter}>
              <span>Gap</span>
              <select
                value={filters.missing}
                onChange={(e) => onFilterChange({ missing: e.target.value })}
              >
                {MISSING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <span className={styles.opCount}>
              {filtered.length} lead{filtered.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      </header>

      <div className={styles.opRows} role="listbox" aria-label="Lista de leads">
        {loading ? (
          <div className={styles.opEmpty}>Cargando leads…</div>
        ) : filtered.length === 0 ? (
          <div className={styles.opEmpty}>Ningún lead coincide con los filtros.</div>
        ) : (
          filtered.map((l, i) => {
            const active = l.id === selectedId;
            const missing = countMissing(l);
            return (
              <div
                key={l.id}
                className={`${styles.opRow} ${active ? styles.opRowFeatured : ''}`}
                onClick={() => onSelectLead(l.id)}
                role="option"
                tabIndex={0}
                aria-selected={active}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectLead(l.id);
                  }
                }}
              >
                <span className={styles.opRowIndex}>{pad2(i + 1)}</span>
                <div className={styles.opRowCore}>
                  <div className={styles.opRowName}>
                    {l.companyName || 'Sin nombre'}
                  </div>
                  <div className={styles.opRowMeta}>
                    {missing > 0 && (
                      <>
                        <span className={styles.opGap}>
                          {missing} falt{missing === 1 ? 'a' : 'an'}
                        </span>
                        <span className={styles.sep}>·</span>
                      </>
                    )}
                    {l.sector || 'Sin sector'}
                    {l.region && (
                      <>
                        <span className={styles.sep}>·</span>
                        {l.region}
                      </>
                    )}
                  </div>
                </div>
                <div
                  className={`${styles.opTier} ${l.tier ? '' : styles.isEmpty}`}
                >
                  <span className={styles.opTierNum}>
                    {l.tier ? l.tier.replace('T', '') : '-'}
                  </span>
                  <span className={styles.opTierLabel}>Tier</span>
                </div>
                <div className={styles.opStage}>
                  <span className={styles.opStageDot} aria-hidden="true" />
                  {stageLabel(l.stage)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function LeadDossier({
  selection,
  ops,
  lastEnrich,
  lastOutreach,
  onTransition,
  onEnrich,
  onApplyAll,
  onOutreach,
  onCopyOutreach,
  onFollowup,
  onDismiss,
}: {
  selection: SelectionState;
  ops: OpsState;
  lastEnrich: LeadActivity | null;
  lastOutreach: LeadActivity | null;
  onTransition: (s: string) => void;
  onEnrich: () => void;
  onApplyAll: () => void;
  onOutreach: () => void;
  onCopyOutreach: () => void;
  onFollowup: () => void;
  onDismiss: () => void;
}) {
  const { id, lead, activities, panelLoading } = selection;
  const { result, busy } = ops;

  return (
    <aside className={styles.opDossier} aria-label="Detalle del lead">
      {!id || panelLoading ? (
        <div className={styles.opDossierEmpty}>
          <p className={styles.opKicker}>Dossier</p>
          <p className={styles.opEmptyCopy}>
            {panelLoading
              ? 'Abriendo expediente…'
              : 'Selecciona una fila para abrir el expediente del lead.'}
          </p>
        </div>
      ) : !lead ? (
        <div className={styles.opDossierEmpty}>
          <p className={styles.opKicker}>Dossier</p>
          <p className={styles.opEmptyCopy}>No pudimos abrir este lead.</p>
        </div>
      ) : (
        <>
          <DossierHeader lead={lead} />

          <div className={styles.opDossBody}>
            <TransitionsSection lead={lead} onTransition={onTransition} />
            <EnrichmentSection lead={lead} lastEnrich={lastEnrich} />
            <ActionsSection
              lead={lead}
              result={result}
              busy={busy}
              lastEnrich={lastEnrich}
              lastOutreach={lastOutreach}
              onEnrich={onEnrich}
              onApplyAll={onApplyAll}
              onOutreach={onOutreach}
              onCopyOutreach={onCopyOutreach}
              onFollowup={onFollowup}
              onDismiss={onDismiss}
            />
            <ActivitySection activities={activities} />
          </div>
        </>
      )}
    </aside>
  );
}

function DossierHeader({ lead }: { lead: LeadWithIntelligence }) {
  return (
    <div className={styles.opDossHead}>
      <div className={styles.opDossEyebrow}>
        {lead.tier && <span>{lead.tier}</span>}
        {lead.tier && <span className={styles.sep}>·</span>}
        <span>{stageLabel(lead.stage)}</span>
      </div>
      <h2 className={styles.opDossName}>
        {lead.companyName || 'Sin nombre'}
      </h2>
      <div className={styles.opDossSub}>
        {lead.sector && <span key="sector">{lead.sector}</span>}
        {lead.region && <span key="region">{lead.region}</span>}
        {lead.contactName && <span key="contactName">{lead.contactName}</span>}
        {lead.contactEmail && (
          <a href={`mailto:${lead.contactEmail}`}>{lead.contactEmail}</a>
        )}
        {lead.contactPhone && (
          <a href={`tel:${lead.contactPhone}`} className={styles.opDossierCall}>
            &#128222; {lead.contactPhone}
          </a>
        )}
        {lead.websiteUrl && (
          (() => {
            const u = lead.websiteUrl!;
            const href = /^https?:/.test(u) ? u : `https://${u}`;
            return (
              <a href={href} target="_blank" rel="noopener">
                {u.replace(/^https?:\/\//, '')}
              </a>
            );
          })()
        )}
      </div>
    </div>
  );
}

function TransitionsSection({
  lead,
  onTransition,
}: {
  lead: LeadWithIntelligence;
  onTransition: (s: string) => void;
}) {
  return (
    <section className={styles.opSect}>
      <div className={styles.opSectLabel}>Transiciones</div>
      {TRANSITIONS[lead.stage]?.length ? (
        <div className={styles.opStages}>
          {TRANSITIONS[lead.stage].map((ts) => {
            const isTerminal = TERMINAL_STAGES.has(ts);
            const isPositive = POSITIVE_STAGES.has(ts);
            return (
              <button
                key={ts}
                className={`${styles.opStageBtn} ${isTerminal ? styles.opStageBtnTerminal : ''} ${isPositive ? styles.opStageBtnPositive : ''}`}
                onClick={() => onTransition(ts)}
              >
                {stageLabel(ts)}
              </button>
            );
          })}
        </div>
      ) : (
        <p className={styles.opNote}>
          Sin transiciones disponibles; este stage es terminal.
        </p>
      )}
    </section>
  );
}

function EnrichmentSection({
  lead,
  lastEnrich,
}: {
  lead: LeadWithIntelligence;
  lastEnrich: LeadActivity | null;
}) {
  return (
    <section className={styles.opSect}>
      <div className={styles.opSectLabel}>Motor de enriquecimiento</div>
      <div className={styles.opEngineHead}>
        <p className={styles.opEngineCaption}>
          Completitud del expediente sobre 8 campos comerciales clave.
        </p>
        <div className={styles.opEngineScore}>
          {completeness(lead)}
          <small>%</small>
        </div>
      </div>
      <div className={styles.opEngineBar}>
        <span style={{ width: `${completeness(lead)}%` }} />
      </div>
      <div className={styles.opFields}>
        {ENRICH_FIELDS.map((f) => {
          const v = lead[f.key as keyof LeadWithIntelligence];
          const filled = v && String(v).trim();
          return (
            <div className={styles.opField} key={f.key}>
              <div className={styles.opFieldName}>{f.label}</div>
              <div
                className={`${styles.opFieldValue} ${filled ? '' : styles.isEmpty}`}
              >
                {filled ? String(v) : 'falta'}
              </div>
            </div>
          );
        })}
      </div>
      {(() => {
        const prov = [];
        if (lead.apifyEnrichedAt)
          prov.push({ label: 'Apify', when: timeAgo(lead.apifyEnrichedAt) });
        if (lead.contactEnrichedAt)
          prov.push({
            label: 'Contact',
            when: timeAgo(lead.contactEnrichedAt),
          });
        if (lastEnrich)
          prov.push({ label: 'IA', when: timeAgo(lastEnrich.createdAt) });
        if (prov.length === 0) return null;
        return (
          <div className={styles.opProv}>
            {prov.map((p) => (
              <span key={p.label} className={styles.opProvItem}>
                <em>{p.label}</em> {p.when}
              </span>
            ))}
          </div>
        );
      })()}
    </section>
  );
}

function ActionsSection({
  lead,
  result,
  busy,
  lastEnrich,
  lastOutreach,
  onEnrich,
  onApplyAll,
  onOutreach,
  onCopyOutreach,
  onFollowup,
  onDismiss,
}: {
  lead: LeadWithIntelligence;
  result: Result;
  busy: Set<string>;
  lastEnrich: LeadActivity | null;
  lastOutreach: LeadActivity | null;
  onEnrich: () => void;
  onApplyAll: () => void;
  onOutreach: () => void;
  onCopyOutreach: () => void;
  onFollowup: () => void;
  onDismiss: () => void;
}) {
  return (
    <section className={styles.opSect}>
      <div className={styles.opSectLabel}>Acciones</div>
      <div className={styles.opActions}>
        <Link to={`/leads/${lead.id}`} className={styles.opAction}>
          <span className={styles.opActionGlyph}>&#9998;</span>
          <div className={styles.opActionCopy}>
            <span className={styles.opActionTitle}>Editar lead</span>
            <span className={styles.opActionSub}>Campos y timeline completos.</span>
          </div>
          <span className={styles.opActionBadge}>&rarr;</span>
        </Link>
        <button className={styles.opAction} disabled={busy.has('enrich')} onClick={onEnrich}>
          <span className={styles.opActionGlyph}>01</span>
          <div className={styles.opActionCopy}>
            <span className={styles.opActionTitle}>Enriquecer con IA</span>
            <span className={styles.opActionSub}>Sugerir los campos que faltan.</span>
          </div>
          {lastEnrich && (
            <span className={styles.opActionBadge}>
              hace {timeAgo(lastEnrich.createdAt)}
            </span>
          )}
        </button>
        <button className={styles.opAction} disabled={busy.has('outreach')} onClick={onOutreach}>
          <span className={styles.opActionGlyph}>02</span>
          <div className={styles.opActionCopy}>
            <span className={styles.opActionTitle}>Generar outreach</span>
            <span className={styles.opActionSub}>Borrador de primer email en tu voz.</span>
          </div>
          {lastOutreach && (
            <span className={styles.opActionBadge}>
              hace {timeAgo(lastOutreach.createdAt)}
            </span>
          )}
        </button>
        <button className={styles.opAction} disabled={busy.has('followup')} onClick={onFollowup}>
          <span className={styles.opActionGlyph}>03</span>
          <div className={styles.opActionCopy}>
            <span className={styles.opActionTitle}>Sugerir follow-up</span>
            <span className={styles.opActionSub}>Próxima acción y urgencia.</span>
          </div>
          {lead.nextFollowupAt && (
            <span className={`${styles.opActionBadge} ${styles.isWarn}`}>
              ya en agenda · {timeAgo(lead.nextFollowupAt)}
            </span>
          )}
        </button>
        <Link to={`/leads/${lead.id}`} className={styles.opAction}>
          <span className={styles.opActionGlyph}>05</span>
          <div className={styles.opActionCopy}>
            <span className={styles.opActionTitle}>Ver todo el lead</span>
            <span className={styles.opActionSub}>Página completa del expediente.</span>
          </div>
          <span className={styles.opActionBadge}>&rarr;</span>
        </Link>
      </div>

      {result && (
        <ResultCard
          result={result}
          busy={busy}
          onApplyAll={onApplyAll}
          onCopy={onCopyOutreach}
          onDismiss={onDismiss}
        />
      )}
    </section>
  );
}

function ResultCard({
  result,
  busy,
  onApplyAll,
  onCopy,
  onDismiss,
}: {
  result: Result;
  busy: Set<string>;
  onApplyAll: () => void;
  onCopy: () => void;
  onDismiss: () => void;
}) {
  if (!result) return null;

  if (result.type === 'enrich') {
    const rows = ENRICH_FIELDS.filter(
      (f) => result.data[f.key] != null && result.data[f.key] !== '',
    );
    const any = rows.length > 0;
    return (
      <div className={styles.opResult}>
        <div className={styles.opResultHead}>
          Sugerencias de IA
          {result.confidence ? ` · confianza ${result.confidence}` : ''}
        </div>
        {any ? (
          <div className={styles.opResultFields}>
            {rows.map((f) => (
              <div className={styles.opField} key={f.key}>
                <div className={styles.opFieldName}>{f.label}</div>
                <div className={styles.opFieldValue}>{result.data[f.key]}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.opNote}>Sin sugerencias nuevas.</p>
        )}
        <div className={styles.opResultActions}>
          {any && (
            <button className={styles.opBtn} onClick={onApplyAll} disabled={busy.has('applyAll')}>
              Aplicar todo
            </button>
          )}
          <button className={`${styles.opBtn} ${styles.opBtnGhost}`} onClick={onDismiss}>
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  if (result.type === 'outreach') {
    return (
      <div className={styles.opResult}>
        <div className={styles.opResultHead}>Borrador de outreach</div>
        <div
          style={{
            fontWeight: 600,
            marginBottom: '0.6rem',
            letterSpacing: '-0.005em',
          }}
        >
          {result.data.subject}
        </div>
        <pre>{result.data.body}</pre>
        <div className={styles.opResultActions}>
          <button className={styles.opBtn} onClick={onCopy}>
            Copiar
          </button>
          <button className={`${styles.opBtn} ${styles.opBtnGhost}`} onClick={onDismiss}>
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  if (result.type === 'followup') {
    return (
      <div className={styles.opResult}>
        <div className={styles.opResultHead}>
          Follow-up sugerido · urgencia {result.data.urgency}
        </div>
        <div
          style={{
            fontWeight: 600,
            marginBottom: '0.35rem',
            letterSpacing: '-0.005em',
          }}
        >
          {result.data.action}
        </div>
        <p className={styles.opNote} style={{ margin: 0 }}>
          {result.data.reasoning}
        </p>
        <div className={styles.opResultActions}>
          <button className={`${styles.opBtn} ${styles.opBtnGhost}`} onClick={onDismiss}>
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.opResult}>
      <div className={styles.opResultHead}>Error</div>
      <p className={styles.opNote}>{result.message}</p>
      <div className={styles.opResultActions}>
        <button className={`${styles.opBtn} ${styles.opBtnGhost}`} onClick={onDismiss}>
          Cerrar
        </button>
      </div>
    </div>
  );
}

function ActivitySection({ activities }: { activities: LeadActivity[] }) {
  return (
    <section className={styles.opSect}>
      <div className={styles.opSectLabel}>Actividad reciente</div>
      {activities.length === 0 ? (
        <p className={styles.opNote}>Sin actividad registrada todavía.</p>
      ) : (
        <div className={styles.opActivityList}>
          {activities.slice(0, 6).map((a) => (
            <div key={a.id} className={styles.opActivity}>
              <div className={styles.opActivityWhen}>{timeAgo(a.createdAt)}</div>
              <div className={styles.opActivityBody}>
                <div className={styles.opActivityText}>{a.content || a.type}</div>
                <div className={styles.opActivityType}>
                  {a.type.replace(/_/g, ' ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
